/**
 * Calculator — MS31 cost + time-saved ROI tool at /calculator.
 *
 * Single most effective B2B sales surface: input team size + which
 * celebrations to include, watch the annual cost + hours-saved
 * numbers update live. Helps prospects move from "this looks nice"
 * to "I can justify this internally."
 *
 * Inputs:
 *   - Team size (slider, 5 → 500)
 *   - Which celebrations to include (chip toggles): birthdays,
 *     anniversaries, welcomes, farewells, cultural moments
 *
 * Output (live):
 *   - Celebrations per year (computed from team × selection)
 *   - Annual Lembra cost (celebrations × $99)
 *   - Hours saved per year (celebrations × 42min, the gap between
 *     ~45min DIY and ~3min Lembra brief)
 *   - "Without Lembra you'd spend X hours" framing line
 *
 * Honesty notes baked into the page:
 *   - Estimates are rough — assumes everyone gets a birthday, ~50%
 *     of the team has a work anniversary in any given year (i.e.
 *     mature companies skew higher), 15-20% turnover for welcome
 *     + farewell.
 *   - Cost line says "from $99 each" — concierge-quoted celebrations
 *     can run higher; we don't pretend the calc covers premium.
 *
 * Heidi-system layout: editorial header, two-column layout (inputs
 * left / outputs right on desktop, stacked on mobile), closing CTA.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Check } from "lucide-react";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

/* ─── Pricing constants ──────────────────────────────────────────── */

const STANDARD_PRICE_AUD = 99;
// Estimated time it takes a person to organise one celebration
// without Lembra (research, order, coordinate vendor, write card,
// follow up). Conservative — many take longer.
const DIY_MINUTES_PER_CELEB = 45;
// Time it takes to brief Lembra in chat for one celebration.
const LEMBRA_MINUTES_PER_CELEB = 3;
const TIME_SAVED_PER_CELEB_MIN = DIY_MINUTES_PER_CELEB - LEMBRA_MINUTES_PER_CELEB;

/* ─── Per-occasion frequency models ──────────────────────────────── */

interface Occasion {
  key: string;
  label: string;
  /** Average celebrations per team member per year. */
  multiplier: number;
  description: string;
}

const OCCASIONS: Occasion[] = [
  {
    key: "birthday",
    label: "Birthdays",
    multiplier: 1.0,
    description: "One per person per year.",
  },
  {
    key: "anniversary",
    label: "Work anniversaries",
    multiplier: 0.85, // ~85% of team will hit an anniversary in any given year (after the first year of tenure)
    description: "First year onwards.",
  },
  {
    key: "welcome",
    label: "Welcomes",
    multiplier: 0.18, // ~18% turnover average for AU professional services
    description: "Mark new hires.",
  },
  {
    key: "farewell",
    label: "Farewells",
    multiplier: 0.18,
    description: "When someone moves on.",
  },
  {
    key: "cultural",
    label: "Cultural moments",
    multiplier: 0.6, // ~6 cultural touchpoints per year (Diwali, Lunar NY, Eid, NAIDOC, etc — applied per team, not per person)
    description: "Diwali, Lunar New Year, NAIDOC, Eid, etc.",
  },
];

/* ─── Helpers ────────────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return amount.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
}

function formatHours(minutes: number): string {
  const hours = minutes / 60;
  if (hours < 10) return `${hours.toFixed(1)} hours`;
  return `${Math.round(hours)} hours`;
}

/* ─── Page ───────────────────────────────────────────────────────── */

export default function Calculator() {
  const [teamSize, setTeamSize] = useState(25);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(["birthday", "anniversary", "welcome", "farewell"]),
  );

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const celebrationsPerYear = useMemo(() => {
    let total = 0;
    for (const occ of OCCASIONS) {
      if (selected.has(occ.key)) {
        // Cultural moments are per-team rather than per-person —
        // each one celebrates the whole team at once.
        if (occ.key === "cultural") total += occ.multiplier * 12; // ~6/year × 2 cards/event scale
        else total += occ.multiplier * teamSize;
      }
    }
    return Math.round(total);
  }, [teamSize, selected]);

  const annualCost = celebrationsPerYear * STANDARD_PRICE_AUD;
  const totalMinutesSaved = celebrationsPerYear * TIME_SAVED_PER_CELEB_MIN;

  return (
    <>
      <PageMeta
        title="Calculator. What would Lembra cost for your team?"
        description="Estimate your annual cost + hours saved. Adjust team size and which celebrations matter to your team. Honest, open-ended, no signup required."
        path="/calculator"
      />

      <div style={{ background: "#FAF6F0", minHeight: "100vh" }}>
        {/* ── Top nav ── */}
        <header className="px-6 md:px-8 pt-6 pb-2">
          <div
            className="flex items-center justify-between"
            style={{ maxWidth: 1180, margin: "0 auto" }}
          >
            <Logo size="sm" />
            <Link
              to="/"
              className="text-sm font-medium text-navy/70 hover:text-coral"
              style={{ fontFamily: sansFamily }}
            >
              ← Back to lembra.com.au
            </Link>
          </div>
        </header>

        {/* ── Hero ── */}
        <section
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(48px, 6vw, 72px)",
            paddingBottom: "clamp(32px, 4vw, 48px)",
          }}
        >
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              Calculator · Cost + time
            </div>
            <h1
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2.2rem, 4.5vw, 3.4rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.022em",
                color: "#1D3461",
                margin: 0,
                maxWidth: "20ch",
              }}
            >
              What would Lembra{" "}
              <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
                cost
              </em>{" "}
              your team?
            </h1>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 17,
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.72)",
                marginTop: 18,
                marginBottom: 0,
                maxWidth: "62ch",
              }}
            >
              Drag the slider, toggle the moments that matter to your team.
              Estimates are rough. The concierge prices anything bigger than
              standard catalogue items separately.
            </p>
          </div>
        </section>

        {/* ── Calculator (inputs left / outputs right on desktop) ── */}
        <section
          className="px-6 md:px-8"
          style={{ paddingBottom: "clamp(56px, 7vw, 96px)" }}
        >
          <div
            className="grid gap-6 md:grid-cols-[1fr_1fr] md:gap-8"
            style={{ maxWidth: 1080, margin: "0 auto" }}
          >
            {/* INPUTS */}
            <div
              className="rounded-2xl bg-white p-7 md:p-8"
              style={{
                border: "1px solid rgba(29,52,97,0.08)",
                boxShadow: "0 12px 32px -20px rgba(29,52,97,0.18)",
              }}
            >
              <div
                aria-hidden="true"
                className="mb-6"
                style={{
                  width: 32,
                  height: 2,
                  background: "#C4564A",
                  borderRadius: 999,
                }}
              />
              <p
                style={{
                  fontFamily: monoFamily,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "rgba(29,52,97,0.55)",
                  marginBottom: 16,
                }}
              >
                Your team
              </p>

              {/* Team size slider */}
              <label className="block">
                <div className="flex items-baseline justify-between">
                  <span
                    className="text-sm font-medium text-navy"
                    style={{ fontFamily: sansFamily }}
                  >
                    Team size
                  </span>
                  <span
                    className="text-2xl font-semibold text-navy tabular-nums"
                    style={{ fontFamily: displayFamily, fontStyle: "italic" }}
                  >
                    {teamSize}
                  </span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={500}
                  step={5}
                  value={teamSize}
                  onChange={(e) => setTeamSize(Number(e.target.value))}
                  aria-label={`Team size: ${teamSize} people`}
                  className="mt-3 w-full accent-coral"
                  style={{ accentColor: "#C4564A" }}
                />
                <div
                  className="mt-1 flex justify-between text-[10px] text-charcoal/45"
                  style={{ fontFamily: monoFamily, letterSpacing: "0.14em" }}
                >
                  <span>5</span>
                  <span>500</span>
                </div>
              </label>

              {/* Occasion toggles */}
              <div className="mt-8">
                <p
                  className="mb-4 text-sm font-medium text-navy"
                  style={{ fontFamily: sansFamily }}
                >
                  Moments to celebrate
                </p>
                <div className="space-y-2">
                  {OCCASIONS.map((occ) => {
                    const active = selected.has(occ.key);
                    return (
                      <button
                        key={occ.key}
                        type="button"
                        onClick={() => toggle(occ.key)}
                        aria-pressed={active}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-coral ${
                          active
                            ? "bg-coral/8 border-coral/30"
                            : "bg-white border-navy/10 hover:border-navy/25"
                        }`}
                        style={{
                          border: active
                            ? "1px solid rgba(196,86,74,0.3)"
                            : "1px solid rgba(29,52,97,0.1)",
                          fontFamily: sansFamily,
                        }}
                      >
                        <div>
                          <p
                            className="text-sm font-semibold"
                            style={{
                              color: active ? "#C4564A" : "#1D3461",
                            }}
                          >
                            {occ.label}
                          </p>
                          <p className="text-xs text-charcoal/60">
                            {occ.description}
                          </p>
                        </div>
                        <span
                          className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                            active ? "bg-coral" : "bg-navy/8"
                          }`}
                        >
                          {active && (
                            <Check
                              size={11}
                              strokeWidth={2.5}
                              className="text-white"
                            />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* OUTPUTS */}
            <div
              className="rounded-2xl p-7 md:p-8 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, #1D3461 0%, #1D3461 70%, #2a4a85 100%)",
                color: "#FAF6F0",
                boxShadow: "0 16px 40px -20px rgba(29,52,97,0.45)",
              }}
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 480px 280px at 90% 10%, rgba(247,232,164,0.08), transparent 60%)",
                }}
              />
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="mb-6"
                  style={{
                    width: 32,
                    height: 2,
                    background: "#F7E8A4",
                    borderRadius: 999,
                  }}
                />
                <p
                  style={{
                    fontFamily: monoFamily,
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "rgba(247,232,164,0.85)",
                    marginBottom: 18,
                  }}
                >
                  Estimate
                </p>

                {/* Celebrations / year */}
                <div className="mb-7">
                  <p
                    className="text-xs uppercase text-cream/55"
                    style={{
                      fontFamily: monoFamily,
                      letterSpacing: "0.16em",
                    }}
                  >
                    Celebrations per year
                  </p>
                  <p
                    style={{
                      fontFamily: displayFamily,
                      fontStyle: "italic",
                      fontWeight: 500,
                      fontSize: "clamp(3rem, 6vw, 4.4rem)",
                      lineHeight: 1,
                      letterSpacing: "-0.025em",
                      color: "#FAF6F0",
                      margin: "6px 0 0",
                    }}
                  >
                    <span className="tabular-nums">{celebrationsPerYear}</span>
                  </p>
                </div>

                {/* Annual cost */}
                <div className="mb-7 pb-7" style={{ borderBottom: "1px solid rgba(250,246,240,0.12)" }}>
                  <p
                    className="text-xs uppercase text-cream/55"
                    style={{
                      fontFamily: monoFamily,
                      letterSpacing: "0.16em",
                    }}
                  >
                    Annual cost (from)
                  </p>
                  <p
                    style={{
                      fontFamily: displayFamily,
                      fontWeight: 500,
                      fontSize: "clamp(2.4rem, 5vw, 3.4rem)",
                      lineHeight: 1.05,
                      letterSpacing: "-0.022em",
                      color: "#FAF6F0",
                      margin: "6px 0 0",
                    }}
                  >
                    <span className="tabular-nums">
                      {formatCurrency(annualCost)}
                    </span>
                  </p>
                  <p
                    className="mt-2 text-xs text-cream/55"
                    style={{ fontFamily: sansFamily }}
                  >
                    {celebrationsPerYear} × ${STANDARD_PRICE_AUD} standard rate. Concierge-quoted
                    upgrades sit on top.
                  </p>
                </div>

                {/* Time saved */}
                <div>
                  <p
                    className="text-xs uppercase text-cream/55"
                    style={{
                      fontFamily: monoFamily,
                      letterSpacing: "0.16em",
                    }}
                  >
                    Hours back per year
                  </p>
                  <p
                    style={{
                      fontFamily: displayFamily,
                      fontWeight: 500,
                      fontSize: "clamp(2rem, 4vw, 2.6rem)",
                      lineHeight: 1.05,
                      letterSpacing: "-0.018em",
                      color: "#F7E8A4",
                      margin: "6px 0 0",
                    }}
                  >
                    <em
                      style={{
                        fontStyle: "italic",
                        fontWeight: 500,
                      }}
                      className="tabular-nums"
                    >
                      {formatHours(totalMinutesSaved)}
                    </em>
                  </p>
                  <p
                    className="mt-2 text-xs leading-relaxed text-cream/65"
                    style={{ fontFamily: sansFamily }}
                  >
                    Without Lembra, organising one celebration takes ~45 minutes
                    (research, order, coordinate vendor, signed card, follow-up).
                    With Lembra: ~3 minutes to brief in chat.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── How we got there ── */}
        <section
          className="px-6 md:px-8"
          style={{
            background: "white",
            paddingTop: "clamp(48px, 6vw, 72px)",
            paddingBottom: "clamp(48px, 6vw, 72px)",
            borderTop: "1px solid rgba(29,52,97,0.06)",
            borderBottom: "1px solid rgba(29,52,97,0.06)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              How we estimate
            </div>
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.75rem, 3vw, 2.2rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                color: "#1D3461",
                margin: 0,
              }}
            >
              The{" "}
              <em style={{ fontStyle: "italic", color: "#C4564A" }}>
                rough maths
              </em>
              .
            </h2>
            <ul
              className="mt-6 space-y-3 text-sm text-navy/75"
              style={{ fontFamily: sansFamily, lineHeight: 1.6 }}
            >
              <li>
                <strong className="text-navy">Birthdays</strong>: one per person per year.
              </li>
              <li>
                <strong className="text-navy">Work anniversaries</strong>: ~85% of the team in any given year (skews higher for mature companies).
              </li>
              <li>
                <strong className="text-navy">Welcomes / farewells</strong>: 18% turnover average (AU professional services).
              </li>
              <li>
                <strong className="text-navy">Cultural moments</strong>: roughly 6 events per year (Diwali, Lunar NY, Eid, NAIDOC, etc), often celebrated team-wide rather than per-person.
              </li>
              <li>
                <strong className="text-navy">Time saved</strong>: 42 minutes per celebration (45 min DIY → 3 min Lembra brief).
              </li>
            </ul>
            <p
              className="mt-6 text-sm text-charcoal/60"
              style={{ fontFamily: sansFamily }}
            >
              These are conservative defaults. Your team will sit somewhere on this curve. The concierge prices anything outside the standard catalogue separately, so the cost figure is a floor, not a ceiling.
            </p>
          </div>
        </section>

        {/* ── Closing CTA ── */}
        <section
          className="px-6 md:px-8 text-center"
          style={{
            background: "#1D3461",
            color: "#FAF6F0",
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(56px, 7vw, 80px)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              aria-hidden="true"
              className="mx-auto mb-7"
              style={{
                width: 48,
                height: 2,
                background: "#C4564A",
                borderRadius: 999,
              }}
            />
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2rem, 4vw, 2.8rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
                color: "#FAF6F0",
                margin: "0 auto 14px",
                maxWidth: "20ch",
              }}
            >
              Try it on a{" "}
              <em style={{ fontStyle: "italic", color: "#F7E8A4" }}>real one</em>.
            </h2>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 17,
                lineHeight: 1.55,
                color: "rgba(250,246,240,0.72)",
                margin: "0 auto 32px",
                maxWidth: "44ch",
              }}
            >
              Brief Lembra with someone celebrating this week. We'll quote it in
              chat, no commitment.
            </p>
            <Link
              to="/chat"
              className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
              style={{
                fontFamily: sansFamily,
                boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
              }}
            >
              Open the concierge
              <ArrowUp size={14} strokeWidth={2.5} style={{ transform: "rotate(45deg)" }} />
            </Link>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ background: "#1D3461", padding: "0 0 36px" }}>
          <div
            className="mx-auto px-6 md:px-8 border-t border-white/10 pt-10 flex flex-col items-center gap-3"
            style={{ maxWidth: 1180 }}
          >
            <Logo size="sm" onDark />
            <p className="text-xs text-white/50" style={{ fontFamily: sansFamily }}>
              © 2026 Lembra. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
